<script setup lang="ts">
import { CopyDocument } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { computed, inject, ref, watch } from "vue";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import { FormElementType, NumberFormElement } from "~/api";
import { showError } from "~/utils/handleAPIError";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<NumberFormElement>>();

const { useAnswer, substituteTemplatedText } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.NUMBER>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);
const localAnswer = ref<number | undefined>(answer.value);
const errorMsg = ref<string | null>(null);

watch(
  () => answer.value,
  (newValue) => {
    localAnswer.value = newValue;
  },
);

const handleUpdateAnswer = () => {
  if (
    localAnswer.value !== undefined &&
    elementProperties.value?.min !== undefined &&
    elementProperties.value?.min !== null &&
    localAnswer.value < elementProperties.value?.min
  ) {
    errorMsg.value = `Error Saving: Must be greater than or equal to ${elementProperties.value?.min}`;
    return;
  } else if (
    localAnswer.value !== undefined &&
    elementProperties.value?.max !== undefined &&
    elementProperties.value?.max !== null &&
    localAnswer.value > elementProperties.value?.max
  ) {
    errorMsg.value = `Error Saving: Must be less than or equal to ${elementProperties.value?.max}`;
    return;
  }

  errorMsg.value = null;
  answer.value = localAnswer.value;
};

const copyTextToClipboard = async () => {
  if (localAnswer.value !== undefined) {
    try {
      await navigator.clipboard.writeText(localAnswer.value.toString());
      ElMessage.success("Copied to clipboard");
    } catch {
      showError("Failed to copy to clipboard. Please try again.");
    }
  }
};

watch(answer, (newValue) => {
  localAnswer.value = newValue;
});
</script>

<template id="form-number-component">
  <div class="NumberComponent">
    <slot name="label" :element="modelValue" />

    <div class="number-component-input">
      <el-icon
        v-if="elementProperties.enableCopy && localAnswer"
        title="Copy to clipboard"
        class="copyToClipboard"
        @click="copyTextToClipboard()"
      >
        <CopyDocument />
      </el-icon>
      <el-input-number
        v-model="localAnswer"
        :class="{ invalid: errorMsg }"
        :precision="elementProperties.precision ?? undefined"
        :controls="elementProperties.showControls ?? undefined"
        controls-position="right"
        :disabled="isReadOnly"
        :placeholder="
          substituteTemplatedText(modelValue.properties.placeholder) ?? ''
        "
        @change="handleUpdateAnswer"
        @click="isReadOnly ? copyTextToClipboard() : undefined"
      />
    </div>
  </div>
  <div class="number-component-error-msg">{{ errorMsg }}</div>
</template>

<style lang="less">
.NumberComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: space-between;

  .number-component-input {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
  }

  .el-input-number.is-center .el-input__inner {
    text-align: right;
  }

  .invalid {
    --el-input-border-color: var(--el-color-danger);
    --el-border-color: var(--el-color-danger);
  }

  .el-input.is-disabled {
    .el-input__wrapper {
      background-color: #f2f2f2;
    }
    .el-input__inner {
      -webkit-text-fill-color: #444142;
    }
  }
}

.number-component-error-msg {
  color: var(--el-color-danger);
  font-size: 0.9rem;
  font-style: italic;
  text-align: right;
}
</style>
